import {
  currentThemeSchemaVersion,
  currentThemeVersion,
  themeMigrationRegistry,
  validateThemePreference,
  type ThemePreference,
  type ThemeSource,
  type ThemeValues,
} from "@inteacc/theme";

export const SHOWCASE_THEME_STORAGE_KEY =
  "iaf:showcase:theme-preference:v2:atlas:showcase-user";

const timestamp = "2026-07-23T00:00:00.000Z";

const preference = (
  key: string,
  values: ThemeValues,
  scope: ThemePreference["scope"],
  actor: string,
): ThemePreference => ({
  key,
  scope,
  schemaVersion: currentThemeSchemaVersion,
  themeVersion: currentThemeVersion,
  values,
  compatibility: {
    schemaVersion: currentThemeSchemaVersion,
    themeVersion: currentThemeVersion,
    minimumSupportedIafVersion: "0.1.0",
    migrationStatus: "not-required",
  },
  revision: 0,
  createdBy: actor,
  createdAt: timestamp,
  updatedBy: actor,
  updatedAt: timestamp,
});

export const showcaseUserScope = {
  kind: "user" as const,
  productId: "framework-showcase",
  tenantId: "atlas",
  userId: "showcase-user",
};

export const createShowcaseUserPreference = (
  values: ThemeValues = {},
): ThemePreference =>
  preference("showcase:user:default", values, showcaseUserScope, "showcase-user");

export const createShowcasePreviewPreference = (
  values: ThemeValues = {},
): ThemePreference =>
  preference(
    "showcase:preview",
    values,
    {
      kind: "preview",
      productId: "framework-showcase",
      tenantId: "atlas",
      userId: "showcase-user",
      screenId: "theme-studio",
      previewId: "working-copy",
    },
    "showcase-user",
  );

export const showcaseOrganizationSources: readonly ThemeSource[] = [
  {
    sourceId: "showcase:product",
    layer: "product",
    scope: { kind: "product", productId: "framework-showcase" },
    preference: preference(
      "showcase:product",
      {
        "appearance.mode": "system",
        "density.mode": "standard",
        "shape.radius.medium": 10,
      },
      { kind: "product", productId: "framework-showcase" },
      "inteacc-design-system",
    ),
  },
  {
    sourceId: "atlas:tenant-brand",
    layer: "tenant",
    scope: {
      kind: "tenant",
      productId: "framework-showcase",
      tenantId: "atlas",
    },
    preference: preference(
      "atlas:tenant-brand",
      {
        "brand.primary": "#1769aa",
        "brand.secondary": "#087e8b",
        "brand.tenantDisplayName": "Atlas Canada",
      },
      {
        kind: "tenant",
        productId: "framework-showcase",
        tenantId: "atlas",
      },
      "atlas-theme-admin",
    ),
  },
  {
    sourceId: "atlas:role:operations",
    layer: "role",
    scope: {
      kind: "role",
      productId: "framework-showcase",
      tenantId: "atlas",
      roleId: "operations",
      priority: 10,
    },
    preference: preference(
      "atlas:role:operations",
      { "accessibility.enhancedFocus": true },
      {
        kind: "role",
        productId: "framework-showcase",
        tenantId: "atlas",
        roleId: "operations",
        priority: 10,
      },
      "atlas-theme-admin",
    ),
  },
];

export const loadShowcaseUserPreference = (): ThemePreference => {
  if (typeof window === "undefined") return createShowcaseUserPreference();
  const saved = window.localStorage.getItem(SHOWCASE_THEME_STORAGE_KEY);
  if (!saved) return createShowcaseUserPreference();
  try {
    const candidate = JSON.parse(saved) as ThemePreference;
    const migrated = themeMigrationRegistry.migrate(
      candidate,
      new Date().toISOString(),
    );
    const validation = validateThemePreference(migrated.preference, {
      layer: "user",
      sourceId: "showcase:local-storage",
    });
    return validation.canSave
      ? migrated.preference
      : createShowcaseUserPreference();
  } catch {
    return createShowcaseUserPreference();
  }
};

export const persistShowcaseUserPreference = (
  preferenceToSave: ThemePreference,
): void => {
  window.localStorage.setItem(
    SHOWCASE_THEME_STORAGE_KEY,
    JSON.stringify(preferenceToSave),
  );
};

export const removeShowcaseUserPreference = (): void => {
  window.localStorage.removeItem(SHOWCASE_THEME_STORAGE_KEY);
};
